/**
 * Iter208.80 — Public "Bottega Case" Site (Agenzia Immobiliare, crafty/scrapbook).
 *
 * Palette moss/rust/mustard/paper, tipografia Alfa Slab One + Caveat + Nunito Sans.
 * Struttura: util-bar top, header centrato con nav-links a sinistra/destra e logo al centro,
 * hero scrapbook con polaroid ruotate (foto dinamiche degli ultimi 3 immobili),
 * explore section con filter-tags, masonry a 3 colonne di 6 m-item (immobili),
 * stamp-row valori, stat-badge circolari, about photo stack + note gialla,
 * testimonial sticky-note, cta postcard, footer centrato.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSiteLocation, useSiteNavigate, SitePreviewLink as Link } from "@/lib/previewNav";
import { Editable, Removable } from "@/lib/siteEditing";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BC_CSS = `
.bottega-site {
  --paper:#F3ECDD;--paper-2:#EBE2CC;--ink:#2B2318;--moss:#5B6B45;--moss-dark:#414E32;
  --rust:#B5502D;--mustard:#E3A63A;--muted:#7C7360;--line:#D9CBA8;
  margin:0;background:var(--paper);color:var(--ink);
  font-family:'Nunito Sans', -apple-system, sans-serif;
  -webkit-font-smoothing:antialiased;overflow-x:hidden;
  background-image:radial-gradient(rgba(43,35,24,0.05) 1px, transparent 1px);
  background-size:16px 16px;
}
.bottega-site *{box-sizing:border-box;}
.bottega-site h1,.bottega-site h2,.bottega-site h3{font-family:'Alfa Slab One','Nunito Sans',sans-serif;font-weight:400;color:var(--ink);text-wrap:balance;margin:0;line-height:1.15;}
.bottega-site .hand{font-family:'Caveat',cursive;}
.bottega-site .eyebrow{font-family:'Nunito Sans';font-weight:700;font-size:0.72rem;letter-spacing:0.14em;text-transform:uppercase;color:var(--moss-dark);}
.bottega-site a{color:inherit;}
.bottega-site img{max-width:100%;display:block;}
.bottega-site .wrap{max-width:1180px;margin:0 auto;padding:0 32px;}
.bottega-site section{position:relative;}
.bottega-site .util-bar{background:var(--moss-dark);color:#E7E0CC;font-size:0.78rem;padding:8px 0;}
.bottega-site .util-bar .wrap{display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px;}
.bottega-site .util-bar span{margin-right:18px;}
.bottega-site header{background:var(--paper);border-bottom:3px solid var(--ink);position:sticky;top:0;z-index:80;}
.bottega-site .nav{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;padding:20px 32px;max-width:1180px;margin:0 auto;gap:16px;}
.bottega-site .nav-links{display:flex;gap:28px;list-style:none;margin:0;padding:0;}
.bottega-site .nav-links.right{justify-content:flex-end;align-items:center;}
.bottega-site .nav-links a{text-decoration:none;font-weight:600;font-size:0.92rem;color:var(--ink);}
.bottega-site .nav-links a:hover,.bottega-site .nav-links a.active{color:var(--rust);}
.bottega-site .logo{font-family:'Alfa Slab One';font-size:1.3rem;text-align:center;color:var(--ink);white-space:nowrap;text-decoration:none;}
.bottega-site .logo .dot{color:var(--rust);}
.bottega-site .stamp-btn{display:inline-flex;align-items:center;gap:8px;background:var(--paper);color:var(--ink);padding:9px 16px;border:2px dashed var(--ink);border-radius:50%/50%;font-family:'Nunito Sans';font-weight:700;font-size:0.78rem;text-decoration:none;transform:rotate(-4deg);white-space:nowrap;cursor:pointer;}
.bottega-site .navmobile{display:none;background:none;border:none;font-size:1.3rem;cursor:pointer;color:var(--ink);}
.bottega-site .mobile-menu{display:none;background:var(--paper-2);border-bottom:3px solid var(--ink);padding:12px 32px;}
.bottega-site .mobile-menu.open{display:block;}
.bottega-site .mobile-menu a{display:block;padding:8px 0;text-decoration:none;color:var(--ink);font-weight:600;border-bottom:1px dashed var(--line);}
.bottega-site .mobile-menu a:last-child{border-bottom:none;}
.bottega-site .hero{padding:90px 0 60px;}
.bottega-site .hero-grid{display:grid;grid-template-columns:1fr 0.95fr;gap:40px;align-items:center;}
.bottega-site .hero-copy .hand{font-size:1.5rem;color:var(--rust);display:block;margin-bottom:6px;}
.bottega-site .hero-copy h1{font-size:clamp(2.1rem,4vw,3.1rem);}
.bottega-site .hero-copy p{font-size:1.05rem;line-height:1.65;color:var(--muted);max-width:440px;margin:22px 0 30px;}
.bottega-site .btn{display:inline-flex;align-items:center;gap:8px;background:var(--rust);color:#FBF6EA;padding:13px 26px;border-radius:100px;font-weight:700;font-size:0.9rem;text-decoration:none;border:2px solid var(--ink);box-shadow:4px 4px 0 var(--ink);transition:transform .12s ease, box-shadow .12s ease;cursor:pointer;}
.bottega-site .btn:hover{transform:translate(2px,2px);box-shadow:2px 2px 0 var(--ink);}
.bottega-site .btn-outline{background:var(--paper);color:var(--ink);}
.bottega-site .polaroids{position:relative;height:420px;}
.bottega-site .polaroid{position:absolute;background:#fff;border:1px solid rgba(43,35,24,0.15);padding:10px 10px 30px;box-shadow:0 14px 24px -14px rgba(43,35,24,0.4);}
.bottega-site .polaroid svg,.bottega-site .polaroid img{width:100%;height:calc(100% - 30px);display:block;object-fit:cover;}
.bottega-site .polaroid .cap{position:absolute;bottom:8px;left:14px;right:14px;font-family:'Caveat';font-weight:700;font-size:1.15rem;color:var(--ink);}
.bottega-site .polaroid.p1{width:220px;height:180px;top:0;left:20px;transform:rotate(-7deg);z-index:2;}
.bottega-site .polaroid.p2{width:200px;height:230px;top:60px;right:0;transform:rotate(5deg);z-index:3;}
.bottega-site .polaroid.p3{width:180px;height:150px;bottom:0;left:80px;transform:rotate(3deg);z-index:1;}
.bottega-site .explore{padding:20px 0 100px;}
.bottega-site .filter-tags{display:flex;flex-wrap:wrap;gap:12px;justify-content:center;background:var(--paper-2);border:2px dashed var(--line);border-radius:16px;padding:26px 30px;}
.bottega-site .filter-tags .grp-label{font-family:'Nunito Sans';font-weight:700;font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);width:100%;text-align:center;margin-bottom:6px;}
.bottega-site .tag-pill{background:#fff;color:var(--ink);border:2px solid var(--ink);border-radius:100px;padding:8px 16px;font-weight:700;font-size:0.86rem;text-decoration:none;cursor:pointer;transition:background .15s ease,color .15s ease;}
.bottega-site .tag-pill:hover,.bottega-site .tag-pill.active{background:var(--rust);color:#fff;}
.bottega-site .sec-head{text-align:center;max-width:560px;margin:0 auto 60px;}
.bottega-site .sec-head h2{font-size:clamp(1.9rem,3vw,2.6rem);margin-top:12px;}
.bottega-site .listings{padding:20px 0 60px;}
.bottega-site .masonry{column-count:3;column-gap:28px;}
.bottega-site .m-item{break-inside:avoid;margin-bottom:34px;background:#fff;border:1px solid rgba(43,35,24,0.12);padding:10px;box-shadow:0 10px 22px -14px rgba(43,35,24,0.4);position:relative;display:block;text-decoration:none;color:var(--ink);}
.bottega-site .m-item:nth-child(3n+1){transform:rotate(-2deg);}
.bottega-site .m-item:nth-child(3n+2){transform:rotate(1.5deg);}
.bottega-site .m-item:nth-child(3n){transform:rotate(-1deg);}
.bottega-site .m-item svg,.bottega-site .m-item img{width:100%;display:block;}
.bottega-site .m-item .m-media{aspect-ratio:4/3;background:var(--paper-2);overflow:hidden;position:relative;}
.bottega-site .m-item .m-media img,.bottega-site .m-item .m-media svg{width:100%;height:100%;object-fit:cover;}
.bottega-site .m-price-tag{background:var(--mustard);color:var(--ink);font-family:'Alfa Slab One';font-size:0.95rem;padding:5px 12px;position:absolute;top:14px;right:14px;transform:rotate(3deg);border:1.5px solid var(--ink);z-index:2;}
.bottega-site .m-title{font-family:'Nunito Sans';font-weight:700;font-size:1.02rem;margin:10px 0 4px;padding:0 4px;}
.bottega-site .m-loc{font-size:0.85rem;color:var(--muted);margin-bottom:10px;padding:0 4px;}
.bottega-site .m-meta{display:flex;gap:14px;font-size:0.78rem;color:var(--ink);border-top:1px dashed var(--line);padding:10px 4px 0;flex-wrap:wrap;}
.bottega-site .values{padding:20px 0 100px;}
.bottega-site .stamp-row{display:flex;justify-content:space-between;gap:24px;flex-wrap:wrap;}
.bottega-site .stamp{flex:1;min-width:220px;text-align:center;padding:30px 20px;border:3px dashed var(--moss);border-radius:16px;background:var(--paper-2);}
.bottega-site .stamp .icon-circ{width:56px;height:56px;border-radius:50%;background:var(--moss);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;}
.bottega-site .stamp .icon-circ svg{width:28px;height:28px;stroke:#fff;fill:none;}
.bottega-site .stamp h3{font-size:1.1rem;margin-bottom:8px;}
.bottega-site .stamp p{font-size:0.9rem;color:var(--muted);line-height:1.6;margin:0;}
.bottega-site .stats{padding:20px 0 100px;}
.bottega-site .stats-row{display:flex;justify-content:center;gap:44px;flex-wrap:wrap;}
.bottega-site .stat-badge{width:130px;height:130px;border-radius:50%;border:3px solid var(--ink);display:flex;flex-direction:column;align-items:center;justify-content:center;background:#fff;}
.bottega-site .stat-badge:nth-child(2){transform:translateY(-16px);background:var(--mustard);}
.bottega-site .stat-badge:nth-child(4){transform:translateY(14px);}
.bottega-site .stat-badge .num{font-family:'Alfa Slab One';font-size:1.5rem;}
.bottega-site .stat-badge .lbl{font-size:0.68rem;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;color:var(--muted);margin-top:4px;text-align:center;padding:0 10px;}
.bottega-site .about-preview{padding:20px 0 100px;}
.bottega-site .about-grid{display:grid;grid-template-columns:0.9fr 1fr;gap:60px;align-items:center;}
.bottega-site .stack{position:relative;height:320px;}
.bottega-site .stack .ph{position:absolute;width:230px;height:280px;background:#fff;padding:10px 10px 30px;border:1px solid rgba(43,35,24,0.15);box-shadow:0 14px 24px -12px rgba(43,35,24,0.35);}
.bottega-site .stack .ph svg{width:100%;height:100%;}
.bottega-site .stack .ph.s1{left:0;top:20px;transform:rotate(-6deg);z-index:1;}
.bottega-site .stack .ph.s2{left:110px;top:0;transform:rotate(4deg);z-index:2;}
.bottega-site .note{background:var(--mustard);padding:26px 28px;border-radius:4px;position:relative;transform:rotate(-1deg);box-shadow:0 10px 20px -12px rgba(43,35,24,0.4);}
.bottega-site .note::before{content:"";position:absolute;width:60px;height:22px;background:rgba(255,255,255,0.55);top:-10px;left:30px;transform:rotate(-4deg);}
.bottega-site .note p.hand{font-size:1.5rem;line-height:1.4;color:var(--ink);margin:0;}
.bottega-site .about-copy .link-arrow{display:inline-flex;margin-top:16px;align-items:center;gap:8px;text-decoration:none;color:var(--ink);font-weight:700;border-bottom:2px solid var(--rust);padding-bottom:2px;}
.bottega-site .testimonial{padding:20px 0 100px;display:flex;justify-content:center;}
.bottega-site .sticky-note{max-width:520px;background:#fff;border:1px solid rgba(43,35,24,0.15);padding:36px 36px 30px;box-shadow:0 16px 30px -16px rgba(43,35,24,0.4);transform:rotate(1.2deg);position:relative;text-align:center;}
.bottega-site .sticky-note .tape{position:absolute;width:70px;height:24px;background:rgba(227,166,58,0.75);top:-12px;left:50%;transform:translateX(-50%) rotate(-3deg);}
.bottega-site .sticky-note p.hand{font-size:1.7rem;line-height:1.4;color:var(--ink);margin:10px 0 18px;}
.bottega-site .sticky-note cite{font-family:'Nunito Sans';font-weight:700;font-size:0.82rem;color:var(--muted);font-style:normal;}
.bottega-site .cta{padding:20px 0 100px;}
.bottega-site .postcard{max-width:900px;margin:0 auto;border:3px dashed var(--ink);border-radius:14px;padding:50px;display:flex;justify-content:space-between;align-items:center;gap:30px;flex-wrap:wrap;background:var(--paper-2);}
.bottega-site .postcard h2{font-size:clamp(1.5rem,2.6vw,2.1rem);max-width:420px;}
.bottega-site .postmark{width:90px;height:90px;border:3px solid var(--rust);border-radius:50%;display:flex;align-items:center;justify-content:center;transform:rotate(-8deg);flex-shrink:0;}
.bottega-site .postmark span{font-family:'Caveat';font-weight:700;color:var(--rust);font-size:1rem;text-align:center;line-height:1.2;}
.bottega-site footer{padding:50px 0 30px;text-align:center;border-top:3px solid var(--ink);}
.bottega-site footer .logo{margin-bottom:14px;display:inline-block;}
.bottega-site .foot-links{display:flex;justify-content:center;gap:26px;margin-bottom:18px;flex-wrap:wrap;}
.bottega-site .foot-links a{text-decoration:none;color:var(--ink);font-weight:600;font-size:0.92rem;}
.bottega-site .foot-links a:hover{color:var(--rust);}
.bottega-site .foot-bottom{font-size:0.78rem;color:var(--muted);}
/* Contatti postcard-style */
.bottega-site .contact-page,.bottega-site .listing-detail-page,.bottega-site .about-full,.bottega-site .annunci-full{padding:60px 0 100px;}
.bottega-site .contact-grid{display:grid;grid-template-columns:1.1fr 0.9fr;gap:56px;align-items:flex-start;}
.bottega-site .postcard-form{background:#fff;border:3px dashed var(--ink);border-radius:14px;padding:36px;position:relative;transform:rotate(-0.4deg);}
.bottega-site .postcard-form::before{content:"POSTA";font-family:'Alfa Slab One';font-size:0.75rem;color:var(--rust);position:absolute;top:14px;right:20px;letter-spacing:0.2em;}
.bottega-site .field-row{margin-bottom:18px;}
.bottega-site .field-row label{display:block;font-family:'Nunito Sans';font-weight:700;font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--moss-dark);margin-bottom:8px;}
.bottega-site .field-row input,.bottega-site .field-row textarea{width:100%;background:var(--paper);border:none;border-bottom:2px dashed var(--line);padding:8px 4px;font-family:'Nunito Sans';font-size:0.95rem;color:var(--ink);outline:none;transition:border-color .2s;}
.bottega-site .field-row input:focus,.bottega-site .field-row textarea:focus{border-color:var(--rust);}
.bottega-site .field-row textarea{min-height:110px;resize:vertical;}
.bottega-site .contact-side{background:var(--paper-2);border:2px dashed var(--line);border-radius:16px;padding:32px 28px;}
.bottega-site .contact-side h3{margin-bottom:14px;font-size:1.15rem;}
.bottega-site .contact-side .row{display:flex;gap:10px;align-items:flex-start;margin:14px 0;font-size:0.95rem;color:var(--ink);}
.bottega-site .contact-side .row svg{width:16px;height:16px;stroke:var(--rust);fill:none;flex-shrink:0;margin-top:2px;}
.bottega-site .listing-hero{aspect-ratio:16/9;background:var(--paper-2);border:1px solid rgba(43,35,24,0.15);overflow:hidden;margin-bottom:32px;padding:10px;box-shadow:0 14px 24px -14px rgba(43,35,24,0.4);}
.bottega-site .listing-hero > *{width:100%;height:100%;object-fit:cover;}
.bottega-site .listing-detail-grid{display:grid;grid-template-columns:1.4fr 1fr;gap:48px;}
.bottega-site .listing-info-panel{background:#fff;border:1px solid rgba(43,35,24,0.15);padding:28px;box-shadow:0 10px 22px -14px rgba(43,35,24,0.4);}
.bottega-site .listing-info-panel .price{font-family:'Alfa Slab One';font-size:1.7rem;color:var(--rust);margin-bottom:8px;}
.bottega-site .listing-info-panel .stats-list{display:grid;grid-template-columns:1fr 1fr;gap:12px 24px;margin:20px 0 24px;padding:16px 0;border-top:1px dashed var(--line);border-bottom:1px dashed var(--line);font-size:0.85rem;color:var(--ink);}
.bottega-site .listing-info-panel .stats-list .k{font-size:0.66rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);display:block;margin-bottom:4px;font-weight:700;}
.bottega-site .back-link{display:inline-flex;align-items:center;gap:8px;font-family:'Nunito Sans';font-weight:700;font-size:0.82rem;color:var(--rust);text-decoration:none;margin-bottom:20px;}
.bottega-site .empty-state{padding:80px 20px;text-align:center;color:var(--muted);font-family:'Caveat';font-size:1.5rem;}
.bottega-site .team-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:32px;margin-top:44px;}
.bottega-site .team-card{background:#fff;border:1px solid rgba(43,35,24,0.15);padding:14px;box-shadow:0 12px 22px -14px rgba(43,35,24,0.4);}
.bottega-site .team-card:nth-child(3n+1){transform:rotate(-1.5deg);}
.bottega-site .team-card:nth-child(3n){transform:rotate(1deg);}
.bottega-site .team-card .team-avatar{aspect-ratio:1;background:var(--moss);display:flex;align-items:center;justify-content:center;font-family:'Alfa Slab One';font-size:2.6rem;color:#fff;margin-bottom:14px;}
.bottega-site .team-card h4{font-family:'Nunito Sans';font-weight:700;font-size:1.05rem;margin:0 0 4px;padding:0 4px;}
.bottega-site .team-card .role{font-family:'Caveat';font-size:1.1rem;color:var(--rust);padding:0 4px 8px;}
@media (max-width: 980px){
  .bottega-site .util-bar .wrap{flex-direction:column;gap:4px;text-align:center;}
  .bottega-site .nav{grid-template-columns:1fr auto;}
  .bottega-site .nav-links{display:none;}
  .bottega-site .navmobile{display:block;}
  .bottega-site .hero-grid{grid-template-columns:1fr;}
  .bottega-site .polaroids{height:320px;margin-top:20px;}
  .bottega-site .masonry{column-count:1;}
  .bottega-site .stamp-row{flex-direction:column;}
  .bottega-site .stats-row{gap:24px;}
  .bottega-site .stat-badge{width:110px;height:110px;}
  .bottega-site .about-grid,.bottega-site .contact-grid,.bottega-site .listing-detail-grid{grid-template-columns:1fr;gap:32px;}
  .bottega-site .stack{height:260px;}
  .bottega-site .postcard{flex-direction:column;text-align:center;}
  .bottega-site .team-grid{grid-template-columns:1fr;}
}
`;

function PolaroidArt({ variant = 0 }) {
  const bgs = ["#5B6B45","#B5502D","#E3A63A"];
  return (
    <svg viewBox="0 0 300 220" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="300" height="220" fill={bgs[variant % 3]}/>
      <g fill="#2B2318" opacity="0.85">
        <path d="M40 180 L40 110 L150 60 L260 110 L260 180 Z"/>
        <rect x="80" y="130" width="40" height="50"/>
        <rect x="170" y="130" width="40" height="50"/>
      </g>
    </svg>
  );
}

function MItemArt({ variant = 0 }) {
  const bgs = ["#5B6B45","#B5502D","#E3A63A","#7C7360","#EBE2CC","#5B6B45"];
  return (
    <svg viewBox="0 0 300 220" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="300" height="220" fill={bgs[variant % 6]}/>
      <g fill="#2B2318" opacity="0.85">
        <path d="M40 180 L40 100 L150 50 L260 100 L260 180 Z"/>
        <rect x="80" y="130" width="40" height="50"/>
        <rect x="180" y="130" width="40" height="50"/>
      </g>
    </svg>
  );
}

function BCHeader({ slug, studio, activePage }) {
  const base = `/site/${slug}`;
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useSiteLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  return (
    <>
      <div className="util-bar" data-testid="bc-util-bar">
        <div className="wrap">
          <span>
            {studio.phone && <>✆ {studio.phone} &nbsp; </>}
            {studio.email && <>✉ {studio.email}</>}
          </span>
          <span>Instagram · Facebook</span>
        </div>
      </div>
      <header data-testid="bc-header">
        <div className="nav">
          <ul className="nav-links">
            <li><Link to={base} className={activePage === "home" ? "active" : ""} data-testid="bc-nav-home">Home</Link></li>
            <li><Link to={`${base}/annunci`} className={activePage === "annunci" ? "active" : ""} data-testid="bc-nav-annunci">Annunci</Link></li>
          </ul>
          <Link to={base} className="logo" data-testid="bc-logo">{studio.name || "Bottega Case"}<span className="dot">.</span></Link>
          <ul className="nav-links right">
            <li><Link to={`${base}/chi-siamo`} className={activePage === "chi-siamo" ? "active" : ""} data-testid="bc-nav-chi-siamo">Chi siamo</Link></li>
            <li><Link to={`${base}/contatti`} data-testid="bc-nav-contatti"><span className="stamp-btn">Contatti ✎</span></Link></li>
          </ul>
          <button className="navmobile" onClick={() => setMobileOpen(!mobileOpen)} data-testid="bc-navmobile" aria-label="Menu">☰</button>
        </div>
        <div className={"mobile-menu" + (mobileOpen ? " open" : "")} data-testid="bc-mobile-menu">
          <Link to={base}>Home</Link>
          <Link to={`${base}/annunci`}>Annunci</Link>
          <Link to={`${base}/chi-siamo`}>Chi siamo</Link>
          <Link to={`${base}/contatti`}>Contatti</Link>
        </div>
      </header>
    </>
  );
}

function BCFooter({ slug, studio }) {
  const base = `/site/${slug}`;
  const year = new Date().getFullYear();
  return (
    <footer data-testid="bc-footer">
      <Link to={base} className="logo">{studio.name || "Bottega Case"}<span className="dot" style={{color:"var(--rust)"}}>.</span></Link>
      <div className="foot-links">
        <Link to={base}>Home</Link>
        <Link to={`${base}/annunci`}>Annunci</Link>
        <Link to={`${base}/chi-siamo`}>Chi siamo</Link>
        <Link to={`${base}/contatti`}>Contatti</Link>
      </div>
      <div className="foot-bottom">© {year} {studio.name || "Bottega Case"}{studio.vat ? ` — P.IVA ${studio.vat}` : ""}{studio.address ? ` — ${studio.address}` : ""}</div>
    </footer>
  );
}

function MItem({ p, slug, index = 0 }) {
  const priceFmt = p.price ? `€ ${Number(p.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <Link to={`/site/${slug}/annunci/${p.id}`} className="m-item" data-testid={`bc-m-item-${p.id}`}>
      <div className="m-media">
        <span className="m-price-tag">{priceFmt}</span>
        {p.cover_url ? <img src={p.cover_url} alt={p.address}/> : <MItemArt variant={index}/>}
      </div>
      <div className="m-title">{p.address || "Immobile"}</div>
      <div className="m-loc">{p.city}</div>
      <div className="m-meta">
        {p.surface_m2 ? <span>{p.surface_m2} mq</span> : null}
        {p.rooms ? <span>{p.rooms} locali</span> : null}
        {p.bathrooms ? <span>{p.bathrooms} bagni</span> : null}
      </div>
    </Link>
  );
}

function ContactFormBC({ slug, propertyId, testIdPrefix = "bc-contact" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { toast.error("Nome, email e messaggio obbligatori."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/public/site/${slug}/contact`, { ...form, property_id: propertyId || null });
      setSent(true); setForm({ name:"",email:"",phone:"",message:"" });
      toast.success("Cartolina spedita — ti scriviamo entro un giorno lavorativo.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito.");
    } finally { setSending(false); }
  };
  if (sent) return <div className="postcard-form" data-testid={`${testIdPrefix}-success`} style={{background:"var(--mustard)"}}><h3 style={{fontFamily:"'Alfa Slab One'"}}>Cartolina ricevuta!</h3><p className="hand" style={{fontSize:"1.4rem",marginTop:"14px"}}>Ti scriviamo entro un giorno lavorativo. Grazie 🌿</p></div>;
  return (
    <form onSubmit={submit} className="postcard-form" data-testid={`${testIdPrefix}-form`}>
      <div className="field-row"><label>Nome e cognome</label><input value={form.name} onChange={(e)=>setForm(f=>({...f, name:e.target.value}))} data-testid={`${testIdPrefix}-name`} required/></div>
      <div className="field-row"><label>Email</label><input type="email" value={form.email} onChange={(e)=>setForm(f=>({...f, email:e.target.value}))} data-testid={`${testIdPrefix}-email`} required/></div>
      <div className="field-row"><label>Telefono</label><input value={form.phone} onChange={(e)=>setForm(f=>({...f, phone:e.target.value}))} data-testid={`${testIdPrefix}-phone`}/></div>
      <div className="field-row"><label>Messaggio</label><textarea value={form.message} onChange={(e)=>setForm(f=>({...f, message:e.target.value}))} data-testid={`${testIdPrefix}-message`} required/></div>
      <button type="submit" className="btn" disabled={sending} data-testid={`${testIdPrefix}-submit`}>{sending ? "Invio…" : "Spedisci cartolina"}</button>
    </form>
  );
}

function BCHome({ slug, studio, properties }) {
  const founded = studio.founded_year;
  const anni = founded ? (2026 - Number(founded)) : "—";
  // Polaroid hero: prime 3 proprietà
  const poloProps = (properties || []).slice(0, 3);
  const poloClasses = ["p1", "p2", "p3"];
  const poloCaps = ["Questa mi piace ♥", "Sole al pomeriggio", "Vicino al parco"];
  // Masonry: 6 items dagli ultimi immobili
  const masonry = (properties || []).slice(0, 6);
  return (
    <>
      <Removable sectionKey="hero">
      <section className="hero" data-testid="bc-section-hero">
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <Editable field="bc.hero.hand" as="span" className="hand">Ciao,</Editable>
            <Editable field="bc.hero.h1" as="h1" multiline>{`Le case sono storie.\nTi aiutiamo a scriverne una.`}</Editable>
            <Editable field="bc.hero.p" as="p" multiline>{`Siamo una bottega di famiglia${studio.city ? ` a ${studio.city}` : ""}${founded ? `, dal ${founded}` : ""}. Poche case per volta, seguite come se fossero le nostre.`}</Editable>
            <Link to={`/site/${slug}/annunci`} className="btn" data-testid="bc-hero-cta"><Editable field="bc.hero.cta">Sfoglia le nostre case →</Editable></Link>
          </div>
          <div className="polaroids" data-testid="bc-polaroids">
            {poloProps.length > 0 ? poloProps.map((p, i) => (
              <div key={p.id} className={"polaroid " + poloClasses[i]} data-testid={`bc-polaroid-${i}`}>
                {p.cover_url ? <img src={p.cover_url} alt={p.address}/> : <PolaroidArt variant={i}/>}
                <div className="cap">{poloCaps[i]}</div>
              </div>
            )) : [0, 1, 2].map((i) => (
              <div key={i} className={"polaroid " + poloClasses[i]}>
                <PolaroidArt variant={i}/>
                <div className="cap">{poloCaps[i]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="explore">
      <section className="explore" data-testid="bc-section-explore">
        <div className="wrap">
          <div className="sec-head">
            <span className="eyebrow">Cerca</span>
            <Editable field="bc.explore.h2" as="h2" style={{marginTop:"12px"}}>Cosa stai cercando?</Editable>
          </div>
          <div className="filter-tags" data-testid="bc-filter-tags">
            <div className="grp-label">Tipologia</div>
            <Link to={`/site/${slug}/annunci`} className="tag-pill">Appartamento</Link>
            <Link to={`/site/${slug}/annunci`} className="tag-pill">Villa</Link>
            <Link to={`/site/${slug}/annunci`} className="tag-pill">Attico</Link>
            <Link to={`/site/${slug}/annunci`} className="tag-pill">Rustico</Link>
            <div className="grp-label" style={{marginTop:"18px"}}>Fascia prezzo</div>
            <Link to={`/site/${slug}/annunci`} className="tag-pill">Fino a 200k</Link>
            <Link to={`/site/${slug}/annunci`} className="tag-pill">200k – 400k</Link>
            <Link to={`/site/${slug}/annunci`} className="tag-pill">400k – 700k</Link>
            <Link to={`/site/${slug}/annunci`} className="tag-pill">700k+</Link>
          </div>
        </div>
      </section>
      </Removable>

      {masonry.length > 0 && (
        <Removable sectionKey="listings">
        <section className="listings" data-testid="bc-section-listings">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">Case selezionate</span>
              <Editable field="bc.listings.h2" as="h2" style={{marginTop:"12px"}}>Le nostre preferite del mese</Editable>
            </div>
            <div className="masonry" data-testid="bc-masonry">
              {masonry.map((p, i) => <MItem key={p.id} p={p} slug={slug} index={i}/>)}
            </div>
          </div>
        </section>
        </Removable>
      )}

      <Removable sectionKey="values">
      <section className="values" data-testid="bc-section-values">
        <div className="wrap">
          <div className="sec-head">
            <span className="eyebrow">Come lavoriamo</span>
            <Editable field="bc.values.h2" as="h2" style={{marginTop:"12px"}}>Piccoli dettagli, grandi differenze</Editable>
          </div>
          <div className="stamp-row">
            <div className="stamp" data-testid="bc-stamp-0">
              <div className="icon-circ"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M12 2 3 7v13h6v-6h6v6h6V7z"/></svg></div>
              <Editable field="bc.values.a.h3" as="h3">Poche case, alla volta</Editable>
              <Editable field="bc.values.a.p" as="p" multiline>Seguiamo al massimo dieci pratiche in parallelo. Così restiamo umani.</Editable>
            </div>
            <div className="stamp" data-testid="bc-stamp-1">
              <div className="icon-circ"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M4 21c0-4 3.5-6 8-6s8 2 8 6M12 11a4 4 0 100-8 4 4 0 000 8z"/></svg></div>
              <Editable field="bc.values.b.h3" as="h3">Un referente unico</Editable>
              <Editable field="bc.values.b.p" as="p" multiline>La stessa persona ti segue dalla prima chiamata al rogito.</Editable>
            </div>
            <div className="stamp" data-testid="bc-stamp-2">
              <div className="icon-circ"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M13 2 3 14h8l-1 8 11-14h-8z"/></svg></div>
              <Editable field="bc.values.c.h3" as="h3">Risposte rapide</Editable>
              <Editable field="bc.values.c.p" as="p" multiline>Un giorno lavorativo per rispondere, sempre. Anche via WhatsApp.</Editable>
            </div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="stats">
      <section className="stats" data-testid="bc-section-stats">
        <div className="wrap">
          <div className="stats-row">
            <div className="stat-badge"><div className="num">168</div><div className="lbl">Case vendute</div></div>
            <div className="stat-badge"><div className="num">{anni}</div><div className="lbl">Anni di attività</div></div>
            <div className="stat-badge"><div className="num">4.9</div><div className="lbl">Voto medio</div></div>
            <div className="stat-badge"><div className="num">1gg</div><div className="lbl">Tempo di risposta</div></div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="about-preview">
      <section className="about-preview" data-testid="bc-section-about-preview">
        <div className="wrap about-grid">
          <div className="stack">
            <div className="ph s1"><PolaroidArt variant={0}/></div>
            <div className="ph s2"><PolaroidArt variant={2}/></div>
          </div>
          <div className="about-copy">
            <span className="eyebrow">Chi siamo</span>
            <Editable field="bc.about.h2" as="h2" multiline style={{marginTop:"12px",fontSize:"clamp(1.7rem,2.6vw,2.4rem)"}}>{`Una bottega di famiglia,\nnon un'agenzia qualunque`}</Editable>
            <div className="note" style={{marginTop:"22px"}}>
              <Editable field="bc.about.note" as="p" multiline className="hand">Ti raccontiamo la casa come se te la stessimo consigliando davanti a un caffè.</Editable>
            </div>
            <Link to={`/site/${slug}/chi-siamo`} className="link-arrow">Conosci la nostra storia →</Link>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="testimonial">
      <section className="testimonial" data-testid="bc-section-testimonial">
        <div className="sticky-note">
          <div className="tape"/>
          <Editable field="bc.testimonial.quote" as="p" multiline className="hand">«Non ci hanno venduto una casa. Ci hanno raccontato la loro città finché non ne abbiamo scelta una noi.»</Editable>
          <Editable field="bc.testimonial.cite" as="cite">{`— Elena & Paolo, acquirenti${studio.city ? ` a ${studio.city}` : ""}`}</Editable>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="cta">
      <section className="cta" data-testid="bc-section-cta">
        <div className="wrap">
          <div className="postcard">
            <div style={{flex:1,minWidth:"260px"}}>
              <Editable field="bc.cta.hand" as="span" className="hand" style={{fontSize:"1.6rem",color:"var(--rust)",display:"block",marginBottom:"6px"}}>Ci scrivi?</Editable>
              <Editable field="bc.cta.h2" as="h2" multiline>Raccontaci cosa cerchi. Ti richiamiamo entro un giorno.</Editable>
            </div>
            <div style={{display:"flex",gap:"20px",alignItems:"center",flexWrap:"wrap"}}>
              <div className="postmark" data-testid="bc-postmark"><span>PRONTA<br/>RISPOSTA</span></div>
              <Link to={`/site/${slug}/contatti`} className="btn" data-testid="bc-cta-btn"><Editable field="bc.cta.btn">Scrivici →</Editable></Link>
            </div>
          </div>
        </div>
      </section>
      </Removable>
    </>
  );
}

function BCAnnunci({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/site/${slug}/properties`).then((r) => setItems(r.data.properties || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  }, [slug]);
  const cities = useMemo(() => Array.from(new Set(items.map(i => i.city).filter(Boolean))), [items]);
  const filtered = useMemo(() => items.filter((p) => {
    if (city && p.city !== city) return false;
    if (search && !(p.address || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [items, city, search]);
  return (
    <section className="annunci-full" data-testid="bc-page-annunci">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Il catalogo completo</span>
          <h2 style={{marginTop:"12px"}}>Tutte le nostre case</h2>
        </div>
        <div className="filter-tags" data-testid="bc-annunci-filters" style={{marginBottom:"36px"}}>
          <div className="grp-label">Filtra per zona</div>
          <button type="button" className={"tag-pill" + (city === "" ? " active" : "")} onClick={() => setCity("")} data-testid="bc-filter-all">Tutte</button>
          {cities.map((c) => <button key={c} type="button" className={"tag-pill" + (city === c ? " active" : "")} onClick={() => setCity(c)} data-testid={`bc-filter-${c}`}>{c}</button>)}
        </div>
        <div style={{marginBottom:"24px",textAlign:"center"}}>
          <input placeholder="✎ scrivi un indirizzo…" value={search} onChange={(e)=>setSearch(e.target.value)} data-testid="bc-annunci-search" style={{padding:"12px 18px",background:"#fff",border:"2px dashed var(--ink)",borderRadius:"100px",fontFamily:"'Caveat'",fontSize:"1.2rem",width:"320px",maxWidth:"100%",outline:"none",color:"var(--ink)"}}/>
          <span style={{marginLeft:"16px",fontFamily:"'Caveat'",fontSize:"1.3rem",color:"var(--rust)"}} data-testid="bc-annunci-count">{filtered.length} case</span>
        </div>
        {loading ? <div className="empty-state">Un attimo…</div>
          : filtered.length === 0 ? <div className="empty-state" data-testid="bc-annunci-empty">Niente da vedere qui, per ora ✎</div>
          : <div className="masonry">{filtered.map((p, i) => <MItem key={p.id} p={p} slug={slug} index={i}/>)}</div>}
      </div>
    </section>
  );
}

function BCChiSiamo({ slug, studio }) {
  const founded = studio.founded_year;
  const team = [
    { name: (studio.name || "Bottega").split(" ")[0], role: "Fondatore", initial: (studio.name || "B")[0] },
    { name: "Lucia Verdi", role: "La regina del quartiere", initial: "L" },
    { name: "Tommaso Bianchi", role: "Valutazioni oneste", initial: "T" },
  ];
  return (
    <section className="about-full" data-testid="bc-page-chi-siamo">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">La nostra bottega</span>
          <h2 style={{marginTop:"12px",fontSize:"clamp(2rem,3.4vw,2.8rem)"}}>La nostra storia</h2>
        </div>
        <div className="about-grid">
          <div className="stack">
            <div className="ph s1"><PolaroidArt variant={1}/></div>
            <div className="ph s2"><PolaroidArt variant={2}/></div>
          </div>
          <div className="about-copy">
            <p style={{fontSize:"1.05rem",lineHeight:1.7,color:"var(--ink)",fontFamily:"'Nunito Sans'"}}>
              {studio.name || "Bottega Case"} nasce{founded ? ` nel ${founded}` : ""}{studio.city ? ` a ${studio.city}` : ""} come piccolo studio di famiglia. Da allora non abbiamo mai voluto crescere troppo: preferiamo poche case alla volta, seguite bene, con la stessa cura di chi entra in una bottega artigianale.
            </p>
            <div className="note" style={{marginTop:"22px"}}>
              <p className="hand">Ogni casa ha una sua storia. Ci piace ascoltarla prima di raccontarla.</p>
            </div>
          </div>
        </div>

        <div style={{marginTop:"72px"}}>
          <div className="sec-head">
            <span className="eyebrow">Il team</span>
            <h2 style={{marginTop:"12px"}}>Le persone della bottega</h2>
          </div>
          <div className="team-grid">
            {team.map((m, i) => (
              <div className="team-card" key={i} data-testid={`bc-team-${i}`}>
                <div className="team-avatar">{m.initial}</div>
                <h4>{m.name}</h4>
                <div className="role">{m.role}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BCContatti({ slug, studio }) {
  const mapQuery = encodeURIComponent(studio.address || studio.city || "Bologna");
  return (
    <section className="contact-page" data-testid="bc-page-contatti">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Scrivici</span>
          <h2 style={{marginTop:"12px",fontSize:"clamp(2rem,3.4vw,2.8rem)"}}>Ci mandi una cartolina?</h2>
        </div>
        <div className="contact-grid">
          <ContactFormBC slug={slug} testIdPrefix="bc-contatti"/>
          <aside className="contact-side" data-testid="bc-contatti-side">
            <h3>Dove trovarci</h3>
            {studio.phone && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>{studio.phone}</div>}
            {studio.email && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M4 4h16v16H4z"/><path d="M4 4l8 8 8-8"/></svg>{studio.email}</div>}
            {studio.address && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>{studio.address}</div>}
            <div style={{marginTop:"20px",aspectRatio:"16/10",background:"var(--paper)",border:"2px dashed var(--line)",borderRadius:"10px",overflow:"hidden"}} data-testid="bc-contatti-map">
              <iframe title="Mappa" src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} loading="lazy" style={{width:"100%",height:"100%",border:"none"}}/>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function BCListingDetail({ slug, pid }) {
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/site/${slug}/properties/${pid}`)
      .then((r) => setProp(r.data.property)).catch(() => setProp(null)).finally(() => setLoading(false));
  }, [slug, pid]);
  if (loading) return <section className="listing-detail-page"><div className="wrap empty-state">Un attimo…</div></section>;
  if (!prop) return <section className="listing-detail-page"><div className="wrap empty-state" data-testid="bc-listing-detail-not-found">Non trovata, ma tornerà presto ✎</div></section>;
  const priceFmt = prop.price ? `€ ${Number(prop.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <section className="listing-detail-page" data-testid="bc-page-listing-detail">
      <div className="wrap">
        <Link to={`/site/${slug}/annunci`} className="back-link" data-testid="bc-listing-back">← Torna alle case</Link>
        <div className="listing-hero">{prop.cover_url ? <img src={prop.cover_url} alt={prop.address}/> : <MItemArt variant={0}/>}</div>
        <div className="listing-detail-grid">
          <div>
            <span className="hand" style={{fontSize:"1.5rem",color:"var(--rust)"}}>Questa la conosciamo bene</span>
            <h2 style={{fontSize:"clamp(1.7rem,3vw,2.4rem)",marginTop:"10px"}}>{prop.address}</h2>
            <p style={{color:"var(--muted)",fontSize:"1rem",marginTop:"6px"}}>{prop.city}</p>
            {prop.description && <p style={{fontSize:"1.02rem",lineHeight:1.7,marginTop:"32px"}}>{prop.description}</p>}
          </div>
          <aside className="listing-info-panel" data-testid="bc-listing-info-panel">
            <div className="price">{priceFmt}</div>
            <div className="stats-list">
              {prop.surface_m2 ? <div><span className="k">Superficie</span>{prop.surface_m2} mq</div> : null}
              {prop.rooms ? <div><span className="k">Locali</span>{prop.rooms}</div> : null}
              {prop.bathrooms ? <div><span className="k">Bagni</span>{prop.bathrooms}</div> : null}
              {prop.city ? <div><span className="k">Zona</span>{prop.city}</div> : null}
            </div>
            <h3 style={{fontSize:"1.05rem",marginBottom:"18px"}}>Ci scrivi?</h3>
            <ContactFormBC slug={slug} propertyId={pid} testIdPrefix="bc-listing-contact"/>
          </aside>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
//  Root
// ============================================================================
export default function BottegaCaseSite({ initialData, slug: slugProp }) {
  const location = useSiteLocation();
  const navigate = useSiteNavigate();
  const slug = slugProp;
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (initialData || !slug) return;
    axios.get(`${API}/public/site/${slug}`)
      .then((r) => setData(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug, initialData]);

  const subPath = location.pathname.replace(`/site/${slug}`, "").replace(/^\//, "");
  const [seg1, seg2] = subPath.split("/");
  let activePage = "home";
  if (seg1 === "annunci") activePage = "annunci";
  else if (seg1 === "chi-siamo") activePage = "chi-siamo";
  else if (seg1 === "contatti") activePage = "contatti";

  if (loading) return <div className="bottega-site"><style>{BC_CSS}</style><div className="empty-state" style={{padding:"120px 20px"}}>Un attimo…</div></div>;
  if (notFound || !data) return (
    <div className="bottega-site" data-testid="bc-not-found"><style>{BC_CSS}</style>
      <div className="empty-state" style={{padding:"120px 20px"}}>Sito non trovato. <button onClick={() => navigate("/")} style={{background:"none",border:"none",color:"var(--rust)",cursor:"pointer",textDecoration:"underline",fontFamily:"'Caveat'",fontSize:"1.5rem"}}>Torna a casa</button></div>
    </div>
  );

  const { studio, featured_properties: properties } = data;
  return (
    <div className="bottega-site" data-testid="bc-site-root" data-slug={slug}>
      <style>{BC_CSS}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
      <link href="https://fonts.googleapis.com/css2?family=Alfa+Slab+One&family=Caveat:wght@500;700&family=Nunito+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <BCHeader slug={slug} studio={studio} activePage={activePage}/>
      {(!seg1) ? <BCHome slug={slug} studio={studio} properties={properties}/>
        : seg1 === "annunci" && !seg2 ? <BCAnnunci slug={slug}/>
        : seg1 === "annunci" && seg2 ? <BCListingDetail slug={slug} pid={seg2}/>
        : seg1 === "chi-siamo" ? <BCChiSiamo slug={slug} studio={studio}/>
        : seg1 === "contatti" ? <BCContatti slug={slug} studio={studio}/>
        : <div className="empty-state" style={{padding:"120px 20px"}}>Pagina non trovata.</div>}
      <BCFooter slug={slug} studio={studio}/>
    </div>
  );
}
